import React, { useEffect, useState } from 'react';
import { Card, Select, Spin, Typography, Row, Col } from 'antd';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';

import { AdminService } from '../services/AdminService';

const { Title, Text } = Typography;
const { Option } = Select;

interface FunnelData {
  conneoId: string;
  customerName: string | null;
  totalSessions: number;
  sessionsWithSide: number;
  sessionsWithProductionFiles: number;
}

interface FunnelStatsResponse {
  period: {
    fromDate: string;
    toDate: string;
  };
  funnelStats: FunnelData[];
}

const SessionFunnelWidget: React.FC = () => {
  const [data, setData] = useState<FunnelStatsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState<string>('');

  // Generate month options (current month + 12 previous months)
  const generateMonthOptions = () => {
    const options = [];
    const now = new Date();

    for (let i = 0; i < 13; i++) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const value = `${year}${month}`;
      const label = date.toLocaleDateString('nl-NL', { year: 'numeric', month: 'long' });

      options.push({ value, label });
    }

    return options;
  };

  const monthOptions = generateMonthOptions();

  // Set default to current month
  useEffect(() => {
    if (!selectedMonth) {
      setSelectedMonth(monthOptions[0].value);
    }
  }, [monthOptions, selectedMonth]);

  const fetchFunnelStats = async (month: string) => {
    if (!month) return;

    setLoading(true);
    try {
      const response = await AdminService.getSessionFunnelStats(month);
      setData(response);
    } catch (error) {
      console.error('Error fetching session funnel stats:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedMonth) {
      fetchFunnelStats(selectedMonth);
    }
  }, [selectedMonth]);

  const handleMonthChange = (month: string) => {
    setSelectedMonth(month);
  };

  // Prepare chart data
  const chartData =
    data?.funnelStats.map((item) => ({
      conneoId: item.conneoId,
      Uploadsessies: item.totalSessions,
      'Sessies met side': item.sessionsWithSide,
      'Sessies met productiebestand': item.sessionsWithProductionFiles,
    })) || [];

  // Calculate totals
  const totals = data?.funnelStats.reduce(
    (acc, item) => ({
      totalSessions: acc.totalSessions + item.totalSessions,
      sessionsWithSide: acc.sessionsWithSide + item.sessionsWithSide,
      sessionsWithProductionFiles:
        acc.sessionsWithProductionFiles + item.sessionsWithProductionFiles,
    }),
    { totalSessions: 0, sessionsWithSide: 0, sessionsWithProductionFiles: 0 },
  ) || { totalSessions: 0, sessionsWithSide: 0, sessionsWithProductionFiles: 0 };

  const currentMonthLabel = monthOptions.find((opt) => opt.value === selectedMonth)?.label;

  return (
    <Card>
      <div
        style={{
          marginBottom: 16,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Title level={4}>Upload Statistieken</Title>
        <Select
          value={selectedMonth}
          onChange={handleMonthChange}
          style={{ width: 200 }}
          placeholder="Selecteer maand"
        >
          {monthOptions.map((option) => (
            <Option key={option.value} value={option.value}>
              {option.label}
            </Option>
          ))}
        </Select>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <Spin size="large" />
        </div>
      ) : data ? (
        <>
          <Row gutter={16} style={{ marginBottom: 24 }}>
            <Col span={8}>
              <Card size="small" style={{ textAlign: 'center' }}>
                <Text strong>Uploadsessies</Text>
                <div style={{ fontSize: '24px', color: '#1890ff' }}>{totals.totalSessions}</div>
              </Card>
            </Col>
            <Col span={8}>
              <Card size="small" style={{ textAlign: 'center' }}>
                <Text strong>Sessies met minimaal 1 side</Text>
                <div style={{ fontSize: '24px', color: '#1890ff' }}>
                  {totals.sessionsWithSide}
                </div>
              </Card>
            </Col>
            <Col span={8}>
              <Card size="small" style={{ textAlign: 'center' }}>
                <Text strong>Sessies met productiebestand</Text>
                <div style={{ fontSize: '24px', color: '#1890ff' }}>
                  {totals.sessionsWithProductionFiles}
                </div>
              </Card>
            </Col>
          </Row>

          <Title level={5}>Sessies per Klant - {currentMonthLabel}</Title>
          <div style={{ height: 400, marginBottom: 24 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="conneoId" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="Uploadsessies" fill="#1890ff" />
                <Bar dataKey="Sessies met side" fill="#52c41a" />
                <Bar dataKey="Sessies met productiebestand" fill="#722ed1" />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {data.funnelStats.length > 0 && (
            <div>
              <Title level={5}>Details per Klant</Title>
              <div style={{ maxHeight: 300, overflowY: 'auto' }}>
                {data.funnelStats.map((item) => (
                  <Card key={item.conneoId} size="small" style={{ marginBottom: 8 }}>
                    <Row>
                      <Col span={6}>
                        <Text strong>
                          {item.conneoId}
                          {item.customerName ? ` (${item.customerName})` : ''}
                        </Text>
                      </Col>
                      <Col span={6}>
                        <Text>Uploadsessies: {item.totalSessions}</Text>
                      </Col>
                      <Col span={6}>
                        <Text>Met side: {item.sessionsWithSide}</Text>
                      </Col>
                      <Col span={6}>
                        <Text>Met productiebestand: {item.sessionsWithProductionFiles}</Text>
                      </Col>
                    </Row>
                  </Card>
                ))}
              </div>
            </div>
          )}
        </>
      ) : (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <Text>Geen data beschikbaar voor de geselecteerde periode</Text>
        </div>
      )}
    </Card>
  );
};

export default SessionFunnelWidget;
